import type { MaybeRefOrGetter } from 'vue';
import type { GlobalCollectionId } from '#layers/data-model';
import { formatTitle } from '@directus/format-title';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplayNames } from '~/composables/use-display-names';
import { useProjectSchemaStore } from '~/stores/project-schema';

export interface UseEntitlementNoticeOptions {
	resource: MaybeRefOrGetter<string>;
	action: MaybeRefOrGetter<string>;
	/** When provided, resolves the collection display name for resource interpolation. */
	collectionId?: MaybeRefOrGetter<GlobalCollectionId | undefined>;
	/** 'row' uses permission_fallback; 'collection' uses permission_fallback_collection. */
	scope: MaybeRefOrGetter<'row' | 'collection'>;
}

/**
 * Maps CRUD-adjacent action names to the existing permissions.no_*_access keys so that common
 * actions reuse already-translated copy rather than falling all the way through to the generic
 * template. Only actions with an exact existing key are listed; everything else falls through.
 */
const ACTION_REUSE_MAP: Record<string, string> = {
	create: 'permissions.no_create_access',
	read: 'permissions.no_read_access',
	update: 'permissions.no_update_access',
	delete: 'permissions.no_delete_access',
	connect: 'permissions.no_connect_access',
	disconnect: 'permissions.no_disconnect_access',
};

export function useEntitlementNotice(options: UseEntitlementNoticeOptions) {
	const { t, te } = useI18n();
	const { findCollectionById } = useProjectSchemaStore();
	const { getCollectionDisplayName } = useDisplayNames();

	const notice = computed<string>(() => {
		const resource = toValue(options.resource);
		const action = toValue(options.action);
		const collectionId = toValue(options.collectionId);
		const scope = toValue(options.scope);

		// Step 1: resource + action-specific override key
		const specificKey = `policy.entitlements.${resource}.${action}_no_permission`;

		if (te(specificKey)) {
			return t(specificKey);
		}

		// Step 2: reuse an existing permissions.no_*_access string for well-known CRUD verbs
		const reuseKey = ACTION_REUSE_MAP[action];

		if (reuseKey !== undefined) {
			return t(reuseKey);
		}

		// Step 3: templated fallback — resolve a human action verb and a resource display name
		const actionKey = `policy.action.${action}`;
		const actionVerb = (te(actionKey) ? t(actionKey) : action).toLowerCase();
		const collection = collectionId !== undefined ? findCollectionById(collectionId) : undefined;
		const resourceName = collection ? getCollectionDisplayName(collection) : formatTitle(resource);

		const fallbackKey = scope === 'collection'
			? 'permissions.permission_fallback_collection'
			: 'permissions.permission_fallback';

		return t(fallbackKey, { action: actionVerb, resource: resourceName });
	});

	return { notice };
}
